import { useState, useEffect, useRef } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, ChevronDown, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLock } from "@/context/LockContext";

const primaryLinks = [
  { to: "/our-heart", label: "Our Heart" },
  { to: "/the-mirror", label: "The Mirror" },
  { to: "/feed", label: "Daily Feed" },
  { to: "/words", label: "Word Explorer" },
  { to: "/red-letters", label: "Red Letters" },
  { to: "/roots", label: "Roots" },
];

const moreLinks = [
  { to: "/power-of-words", label: "Power of Words" },
  { to: "/well", label: "The Well" },
  { to: "/pricing", label: "Plans & Pricing" },
  { to: "/common-thread", label: "Common Thread" },
  { to: "/community", label: "Community" },
  { to: "/mirror", label: "Relationship Mirror" },
  { to: "/journal", label: "Truth Journal" },
];

const allLinks = [...primaryLinks, ...moreLinks];

function useOutside(onClose) {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);
  return ref;
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const { user, logout } = useAuth();
  const { openSettings, pinSet } = useLock();
  const navigate = useNavigate();

  const moreRef = useOutside(() => setMoreOpen(false));
  const acctRef = useOutside(() => setAcctOpen(false));

  const doLogout = async () => {
    setOpen(false);
    setAcctOpen(false);
    navigate("/");
    await logout();
  };

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide whitespace-nowrap transition-colors duration-300 hover:text-[#D4AF37] ${
      isActive ? "text-[#D4AF37]" : "text-[#A8A29E]"
    }`;

  return (
    <header data-testid="navbar" className="sticky top-0 z-50 glass border-b border-white/5">
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <span className="h-9 w-9 rounded-full border border-[#D4AF37]/50 grid place-items-center text-[#D4AF37] gold-glow slow-pulse serif text-xl">
            ✦
          </span>
          <span className="serif text-2xl tracking-tight text-[#F5F1E7] whitespace-nowrap">
            Mirrors <span className="text-[#D4AF37]">of Your Soul</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-5">
          {primaryLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`} className={linkClass}>
              {l.label}
            </NavLink>
          ))}

          {/* More dropdown */}
          <div className="relative" ref={moreRef}>
            <button
              data-testid="nav-more"
              onClick={() => setMoreOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-sm text-[#A8A29E] hover:text-[#D4AF37] transition-colors duration-300"
            >
              More <ChevronDown size={14} className={`transition-transform duration-300 ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            {moreOpen && (
              <div className="absolute right-0 mt-3 w-52 glass rounded-xl p-2 border border-white/10 flex flex-col">
                {moreLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-${l.to.slice(1)}`}
                    onClick={() => setMoreOpen(false)}
                    className={({ isActive }) =>
                      `px-3 py-2 rounded-lg text-sm transition-colors duration-300 hover:bg-white/5 ${isActive ? "text-[#D4AF37]" : "text-[#A8A29E]"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {user ? (
            <div className="relative pl-4 border-l border-white/10 flex items-center gap-3" ref={acctRef}>
              {user.tier === "free" && (
                <Link
                  to="/pricing"
                  data-testid="nav-upgrade"
                  className="rounded-full bg-[#D4AF37] text-[#17100C] px-4 py-1.5 text-sm font-medium hover:bg-[#e6c860] transition-colors duration-300"
                >
                  Upgrade
                </Link>
              )}
              <button
                data-testid="nav-account"
                onClick={() => setAcctOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 text-sm text-[#F5F1E7] hover:text-[#D4AF37] transition-colors duration-300"
              >
                <span data-testid="nav-user-name">{user.name}</span>
                <ChevronDown size={14} className={`transition-transform duration-300 ${acctOpen ? "rotate-180" : ""}`} />
              </button>
              {acctOpen && (
                <div className="absolute right-0 top-full mt-3 w-48 glass rounded-xl p-2 border border-white/10 flex flex-col">
                  <NavLink to="/pricing" data-testid="nav-plans" onClick={() => setAcctOpen(false)}
                    className={({ isActive }) => `px-3 py-2 rounded-lg text-sm hover:bg-white/5 ${isActive ? "text-[#D4AF37]" : "text-[#A8A29E]"}`}>
                    Plans & Billing
                  </NavLink>
                  <NavLink to="/lexicon" data-testid="nav-lexicon" onClick={() => setAcctOpen(false)}
                    className={({ isActive }) => `px-3 py-2 rounded-lg text-sm hover:bg-white/5 ${isActive ? "text-[#D4AF37]" : "text-[#A8A29E]"}`}>
                    My Lexicon
                  </NavLink>
                  <NavLink to="/spoken" data-testid="nav-spoken" onClick={() => setAcctOpen(false)}
                    className={({ isActive }) => `px-3 py-2 rounded-lg text-sm hover:bg-white/5 ${isActive ? "text-[#D4AF37]" : "text-[#A8A29E]"}`}>
                    My Words
                  </NavLink>
                  <button data-testid="nav-privacy-lock" onClick={() => { setAcctOpen(false); openSettings(); }}
                    className="text-left px-3 py-2 rounded-lg text-sm text-[#A8A29E] hover:text-[#D4AF37] hover:bg-white/5 inline-flex items-center gap-2">
                    <Lock size={15} /> Privacy Lock
                  </button>
                  <button data-testid="logout-btn" onClick={doLogout}
                    className="text-left px-3 py-2 rounded-lg text-sm text-[#A8A29E] hover:text-[#C1272D] hover:bg-white/5 inline-flex items-center gap-2">
                    <LogOut size={15} /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <button
                data-testid="nav-privacy-lock-guest"
                onClick={openSettings}
                title="Privacy Lock"
                className={`transition-colors duration-300 ${pinSet ? "text-[#D4AF37]" : "text-[#A8A29E] hover:text-[#D4AF37]"}`}
              >
                <Lock size={17} />
              </button>
              <Link
                data-testid="nav-signin"
                to="/login"
                className="rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-1.5 text-sm hover:bg-[#D4AF37]/10 transition-colors duration-300"
              >
                Sign In
              </Link>
            </div>
          )}
        </div>

        <button data-testid="mobile-menu-btn" className="lg:hidden text-[#F5F1E7]" onClick={() => setOpen((v) => !v)}>
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden px-6 pb-6 flex flex-col gap-3 glass max-h-[75vh] overflow-y-auto">
          {allLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `text-base py-1 ${isActive ? "text-[#D4AF37]" : "text-[#A8A29E]"}`}
            >
              {l.label}
            </NavLink>
          ))}
          {user ? (
            <>
              <div className="border-t border-white/10 pt-3" />
              <NavLink to="/lexicon" onClick={() => setOpen(false)}
                className={({ isActive }) => `text-base py-1 ${isActive ? "text-[#D4AF37]" : "text-[#A8A29E]"}`}>
                My Lexicon
              </NavLink>
              <NavLink to="/spoken" onClick={() => setOpen(false)}
                className={({ isActive }) => `text-base py-1 ${isActive ? "text-[#D4AF37]" : "text-[#A8A29E]"}`}>
                My Words
              </NavLink>
              <button data-testid="nav-privacy-lock-mobile" onClick={() => { setOpen(false); openSettings(); }}
                className="text-left text-base py-1 text-[#A8A29E] inline-flex items-center gap-2">
                <Lock size={16} /> Privacy Lock
              </button>
              <button data-testid="logout-btn-mobile" onClick={doLogout} className="text-left text-base py-1 text-[#C1272D]">
                Logout ({user.name})
              </button>
            </>
          ) : (
            <>
              <button data-testid="nav-privacy-lock-mobile-guest" onClick={() => { setOpen(false); openSettings(); }}
                className="text-left text-base py-1 text-[#A8A29E] inline-flex items-center gap-2">
                <Lock size={16} /> Privacy Lock
              </button>
              <Link data-testid="nav-signin-mobile" to="/login" onClick={() => setOpen(false)} className="text-base py-1 text-[#D4AF37]">
                Sign In
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
